import { Box } from '@material-ui/core'
import React from 'react'
import { Divider, FlexBox, Grid } from 'shared/components/Base/Base'
import { Text } from 'shared/components/Text/Text'
import { informationL1, lightGray1, primaryBlack } from 'shared/theme/colors'

import { isConsole } from '../../config/config'
import { formatCurrency } from '../../helpers/formatters'
import { Handler } from '../../helpers/typeHelper'
import { useRepaymentPolicy } from '../../hooks/query/useRepaymentPolicy'
import { useRepaymentsBreakdowns } from '../../hooks/query/useRepaymentsBreakdowns'
import { VendorProgramPolicyType } from '../../models/repayments'
import { ConsoleTeam } from '../../models/team'
import { VendorUser } from '../../models/user'
import { Back } from '../Nav/Back'
import { PageTitle } from '../PageTitle/PageTitle'
import { getPolicyHeader } from './Policy.helper'
import { PolicyDetailCard } from './PolicyDetail/PolicyDetailCard'
import { PolicyDetailHeader } from './PolicyDetail/PolicyDetailHeader'
import { PolicyRefinanceForm } from './PolicyRefinanceForm'

const parseValue = (value: string | undefined): number => {
  if (!value) return 0
  if (isNaN(Number(value))) return 0
  return Number(value)
}

interface PolicyRefinanceBaseProps {
  teamUrl?: string
  policyId: string
  onCancel: Handler
  onPolicyDetail: Handler
}

interface PolicyTeamRefinanceProps {
  team: ConsoleTeam
  vendorUser?: never
}

interface PolicyVendorUserRefinanceProps {
  vendorUser: VendorUser
  team?: never
}

type PolicyRefinanceProps = PolicyRefinanceBaseProps &
  (PolicyTeamRefinanceProps | PolicyVendorUserRefinanceProps)

export function PolicyRefinance({
  team,
  vendorUser,
  policyId,
  teamUrl,
  onCancel,
  onPolicyDetail,
}: PolicyRefinanceProps) {
  const { policy } = useRepaymentPolicy({ teamId: team?.id, vendorId: vendorUser?.vendor.id, policyId })
  const { repaymentsBreakdowns } = useRepaymentsBreakdowns({
    teamId: team?.id,
    vendorId: vendorUser?.vendor.id,
  })
  const totalTeamDebt =
    parseValue(repaymentsBreakdowns?.subscription_breakdown.total_remaining_balance) +
    parseValue(repaymentsBreakdowns?.subscription_breakdown.total_remaining_balance) +
    parseValue(repaymentsBreakdowns?.escrow_breakdown.total_remaining_balance)
  const totalRemainingBalance = parseValue(policy?.repayment_details.remaining_payment)

  if (!policy) return null

  const isSubscription = policy.repayment_details.policy_type === VendorProgramPolicyType.SUBSCRIPTION

  return (
    <>
      {!isConsole() && <Back onClick={onCancel}>{getPolicyHeader(policy)}</Back>}

      <PageTitle title={`Refinance ${getPolicyHeader(policy)}`} />
      <PolicyDetailHeader policy={policy} teamUrl={teamUrl} />

      <Box mt={4} />

      <PolicyDetailCard>
        <Grid columns="1fr 1px 1fr" smColumns="1fr" gap="75px" smGap="40px">
          <PolicyRefinanceForm
            policyId={policyId}
            isSubscription={isSubscription}
            totalRemainingBalance={totalRemainingBalance}
            onCancel={onCancel}
            onPolicyDetail={onPolicyDetail}
            isVendorUser={!!vendorUser}
          />

          <Divider vertical />

          <FlexBox flex={1} isColumn gap="40px" alignItems="flex-start">
            <FlexBox isFullWidth isColumn gap="20px" alignItems="flex-start">
              <Text variant="header3" fontColor={primaryBlack} mb={1}>
                Current deduction details
              </Text>
              {!isSubscription && (
                <TextLineItem
                  title="Total deduction amount"
                  amount={policy?.repayment_details.total_amount_owed}
                />
              )}
              <TextLineItem
                title="Weekly deduction"
                amount={policy?.repayment_details.amount_owed_per_period}
                isHighlighted={isSubscription}
              />
              {!isSubscription && (
                <TextLineItem
                  title="Total remaining balance"
                  amount={totalRemainingBalance.toFixed(2)}
                  isHighlighted
                />
              )}
              {isSubscription && (
                <TextLineItem
                  title="Overdue balance"
                  amount={policy?.repayment_details.underpayment_from_previous_periods_as_of_today}
                />
              )}
            </FlexBox>

            {team && (
              <FlexBox isFullWidth isColumn gap="20px" alignItems="flex-start">
                <Text variant="header3" fontColor={primaryBlack} mb={1}>
                  Fleet liability details
                </Text>
                <TextLineItem title="Last 30 days gross revenue" amount={'-' /* TODO: BE missing */} />
                <TextLineItem title="Last 60 days gross revenue" amount={'-' /* TODO: BE missing */} />
                <TextLineItem
                  title="Average weekly revenue"
                  amount={team?.weekly_revenue_dollars ?? undefined}
                />
                <TextLineItem title="Total team debt" amount={totalTeamDebt.toFixed(2)} />
              </FlexBox>
            )}
          </FlexBox>
        </Grid>
      </PolicyDetailCard>
    </>
  )
}

const TextLineItem = ({
  title,
  amount,
  isHighlighted,
}: {
  title: string
  amount?: string
  isHighlighted?: boolean
}) => (
  <FlexBox
    gap="20px"
    isFullWidth={!isHighlighted}
    width={isHighlighted ? 'calc(100% + 24px)' : '100%'}
    justifyContent="space-between"
    style={isHighlighted ? { backgroundColor: informationL1, margin: -12, padding: 12 } : undefined}
  >
    <Text variant="header3" fontColor={lightGray1}>
      {title}
    </Text>
    <Text variant="header3" fontColor={primaryBlack}>
      {!amount || amount === 'N/A' || isNaN(Number(amount)) ? '-' : formatCurrency(amount)}
    </Text>
  </FlexBox>
)
